import { PurcheStatus } from '../purchases/purchases.types';
import { TablePaginationOptions } from '../tables/tables.types';

/* Libellés FR des catégories métier (miroir de PurchaseEntryFlowStats::CATEGORY_LABELS) */
export const ENTRY_FLOW_CATEGORY_LABELS: Record<string, string> = {
  sellable_direct: 'Vendable direct',
  repair: 'Réparation / production',
  for_parts: 'Pièces détachées',
  rma: 'RMA fournisseur',
  buyback: 'Buyback',
  out_of_stock: 'Sorti du stock (perdu, égaré...)',
  control: 'Contrôle',
  other: 'Autre'
};

export const PURCHASE_STATUS_LABELS: Record<string, string> = {
  negotiation: 'En négociation',
  paid: 'Payée',
  shipping: 'Expédition',
  partially_received: 'Part. reçue',
  received: 'Reçue',
  control: 'En contrôle',
  validated: 'Validée',
  echec: 'Échec',
  rma: 'RMA'
};

export interface EntryFlowCategoryStat {
  count: number;
  pct: number;
}

/* Stats partagées entre le détail d'UNE liste et l'agrégat de PLUSIEURS listes */
export interface EntryFlowStatsCommon {
  devices_total: number;
  devices_completed: number;

  /* Top catégories "métier" à la sortie du contrôle (repair/sellable/for_parts/...), PAS le devenir final réel */
  current_categories: Record<string, EntryFlowCategoryStat>;
  rma_count: number;
  buyback_count: number;

  avg_control_to_exit_hours: number | null;
  avg_reception_to_control_hours: number | null;
  /* Délai sortie de contrôle -> 1ère vente (nil tant qu'aucun device n'a jamais été vendu) */
  avg_exit_to_sale_hours: number | null;
  /* Vieillissement du stock invendu : délai réception -> aujourd'hui, devices pas (encore) vendus */
  avg_unsold_aging_hours: number | null;
  /* Nombre de devices dont le devenir final est une vente (cf. backend #considered_sold?) */
  devices_sold_count: number;
  devices_unsold_count: number;
  /* Device avec un retour rétractation/échange ET actuellement revendu (final_category 'sold') */
  resold_count: number;
  /* Device avec ce même retour mais pas (encore) revendu depuis */
  returned_not_resold_count: number;
  /* Somme HT réellement facturée (via InvoiceItem) pour les devices vendus, hors garantie/batterie/
   * dynamic pricing/commissions marketplace réelles (déjà nette de total_marketplace_commissions) */
  total_sale_price: number;
  /* Commissions marketplace RÉELLES (imports /accounting/commission-imports), déjà déduites de total_sale_price */
  total_marketplace_commissions: number;
  /* Somme HT facturée pour l'option "batterie neuve" des devices vendus */
  total_battery_price: number;
  /* Somme HT facturée pour les extensions de garantie des devices vendus */
  total_warranty_price: number;
  /* total_sale_price + total_battery_price + total_warranty_price */
  total_sale_price_with_options: number;
  total_purchase_price: number;
  /* Prix d'achat des devices dont le devenir final réel n'est pas (encore) 'sold' */
  total_purchase_price_unsold: number;
  avg_purchase_price: number;
}

export interface EntryFlowStatsSummaryRow extends EntryFlowStatsCommon {
  /* Alias de purchase_id, requis par le tableau générique (clé de ligne) */
  id: number;
  purchase_id: number;
  reference_internal: string;
  purchase_status: PurcheStatus;
  supplier: { id: number; name: string } | null;
  created_at: string | null;
  control_started_at: string | null;
  reception_at: string | null;

  expected_quantity: number | null;
  quantity_received: number | null;
  purchase_items_quantity: number;
  purchase_items_not_controlled: number;

  devices_in_control: number;
  devices_without_control_flow: number;
}

/* Version multi-listes de EntryFlowStatsSummaryRow (pas de métadonnées de liste unique) */
export interface EntryFlowStatsAggregateSummary extends EntryFlowStatsCommon {
  purchase_count: number;
  purchase_ids: number[];
  purchases: { id: number; reference_internal: string }[];
}

export interface EntryFlowBreakdownRow {
  final_category?: string;
  final_status?: string;
  current_category?: string;
  current_status?: string;
  post_grading_category?: string;
  post_grading_status?: string;
  label?: string;
  count: number;
  pct: number;
  total_purchase_price: number;
  avg_purchase_price: number;
  avg_control_to_exit_hours: number | null;
}

export interface EntryFlowGradeMatrixRow {
  supplier_grade: string | null;
  okamac_grade: string;
  count: number;
}

export interface EntryFlowFaultyComponent {
  component: string;
  count: number;
  done_count: number;
  pending_count: number;
}

export type EntryFlowDeviceState = 'completed' | 'in_control' | 'no_control_flow';

export interface EntryFlowDeviceRow {
  device_id: number;
  purchase_id: number;
  serial_no: string;
  sku: string;
  purchase_price: number | null;
  okamac_grade: string | null;
  supplier_grade: string | null;
  state: EntryFlowDeviceState;

  control_entered_at?: string;
  exited_at?: string;
  control_to_exit_hours?: number;
  /* Délai sortie contrôle -> 1ère vente, absent si jamais vendu */
  time_to_sale_hours?: number | null;
  current_status?: string;
  current_category?: string;
  post_grading_status?: string;
  post_grading_category?: string;
  /* Devenir final réel (vendu/déstocké/démantelé...), position actuelle du device */
  final_status?: string;
  final_category?: string;
  /* Codes des pièces HS (Task#parts_code, task_type REPAIR) associées au device, avec statut de réparation */
  faulty_components?: { component: string; done: boolean }[];
}

export interface EntryFlowStatsDetail extends EntryFlowStatsSummaryRow {
  category_labels: Record<string, string>;
  /* Libellé affichable indexé par nom technique de statut (ex "production-extract_battery") */
  status_labels: Record<string, string>;
  /* Libellés du devenir final réel (vendu/déstocké/démantelé/en cours/autre) */
  final_disposition_labels: Record<string, string>;
  by_final_category: EntryFlowBreakdownRow[];
  by_final_status: EntryFlowBreakdownRow[];
  by_current_category: EntryFlowBreakdownRow[];
  by_current_status: EntryFlowBreakdownRow[];
  by_post_grading_category: EntryFlowBreakdownRow[];
  by_post_grading_status: EntryFlowBreakdownRow[];
  grades: Record<string, EntryFlowCategoryStat>;
  supplier_grade_vs_okamac: EntryFlowGradeMatrixRow[];
  faulty_components: EntryFlowFaultyComponent[];
}

/* Version multi-listes de EntryFlowStatsDetail, mêmes breakdowns mais sur des devices de plusieurs listes combinées */
export interface EntryFlowStatsAggregateDetail extends EntryFlowStatsAggregateSummary {
  category_labels: Record<string, string>;
  status_labels: Record<string, string>;
  final_disposition_labels: Record<string, string>;
  by_final_category: EntryFlowBreakdownRow[];
  by_final_status: EntryFlowBreakdownRow[];
  by_current_category: EntryFlowBreakdownRow[];
  by_current_status: EntryFlowBreakdownRow[];
  by_post_grading_category: EntryFlowBreakdownRow[];
  by_post_grading_status: EntryFlowBreakdownRow[];
  grades: Record<string, EntryFlowCategoryStat>;
  supplier_grade_vs_okamac: EntryFlowGradeMatrixRow[];
  faulty_components: EntryFlowFaultyComponent[];
}

export interface EntryFlowStatsListOptions {
  filters?: Record<string, unknown>;
  pagination?: TablePaginationOptions;
  options?: Record<string, unknown>;
}

export interface EntryFlowStatsAggregateFilters {
  supplier?: number | string;
  purchase_status?: string;
  from?: string;
  to?: string;
  reception_from?: string;
  reception_to?: string;
  /* Sélection explicite de listes précises : si présent, remplace tous les autres filtres */
  purchase_ids?: number[];
}

/* Filtres de l'onglet Devices (appliqués côté serveur, cumulables au sein d'un même champ) */
export interface EntryFlowDeviceFilters {
  state?: string[];
  current_category?: string[];
  current_status?: string[];
  final_category?: string[];
  final_status?: string[];
  post_grading_category?: string[];
  post_grading_status?: string[];
  /* '__no_grade__' (NO_GRADE) pour les devices sans grade */
  okamac_grade?: string[];
  component?: string[];
}

/* Requête de l'endpoint /purchases/entry-flow-stats/devices : soit purchase_id (mode
 * liste unique), soit les filtres de périmètre de l'agrégat (mêmes que getAggregate) */
export type EntryFlowDevicesQuery = EntryFlowDeviceFilters &
  EntryFlowStatsAggregateFilters & {
    purchase_id?: number;
    page?: number;
    limit?: number;
  };

export interface EntryFlowDeviceFilterOptions {
  current_categories: string[];
  current_statuses: string[];
  final_categories: string[];
  states: EntryFlowDeviceState[];
  okamac_grades: string[];
  components: string[];
}

export interface EntryFlowDevicesPage {
  values: EntryFlowDeviceRow[];
  count: number;
  page: number;
  per_page: number;
  filter_options: EntryFlowDeviceFilterOptions;
}
